<script setup lang="ts">
import AppTypography from './AppTypography.vue';

const {
  errorMessages = undefined,
  resendTime = 300, // 5 mins
  length = 5,
  isResendSuccess = false,
  resendLoading = false,
  showResend = true,
  loading = false,
} = defineProps<{
  errorMessages?: string;
  resendTime?: number;
  length?: number;
  isResendSuccess?: boolean;
  resendLoading?: boolean;
  showResend?: boolean;
  loading?: boolean;
}>();

const emits = defineEmits<{
  (e: 'resend'): void;
}>();

const timerRunning = ref(false);
const timer = ref(resendTime);

watch(
  () => isResendSuccess,
  () => {
    if (isResendSuccess) {
      timerRunning.value = true;
      const timerInterval = setInterval(() => {
        timer.value--;
        if (timer.value <= 0) {
          clearInterval(timerInterval);
          timerRunning.value = false;
        }
      }, 1000);
    }
  }
);

const formattedTime = computed(() => {
  const minutes = Math.floor(timer.value / 60);
  const seconds = timer.value % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

defineExpose({
  formattedTime,
  timerRunning,
});

const otp = defineModel<string>({
  default: '',
});
</script>

<template>
  <div class="otp-container">
    <v-otp-input
      v-model="otp"
      variant="solo"
      bg-color="#FFFCE9"
      data-testid="app-otp"
      data-cy="input-otp"
      :length="length"
      :error="Boolean(errorMessages)"
      autofocus
    />
    <v-messages
      class="text-center"
      color="error"
      :active="Boolean(errorMessages?.length)"
      :messages="errorMessages"
      v-if="errorMessages?.length"
    />

    <div v-if="showResend" class="otp-request text-center d-flex justify-center align-center gap-2">
      <AppTypography element="span" type="">Didn't receive the code?</AppTypography>
      <AppTypography
        v-if="!timerRunning"
        element="span"
        type="body-2"
        data-testid="resend-otp"
        class="font-weight-bold cursor-pointer"
        @click="emits('resend')"
        >Resend Code
      </AppTypography>
      <div v-else>
        <AppTypography class="text-disabled font-weight-bold" element="span" type="headline-1">
          Resend in {{ formattedTime }}
        </AppTypography>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.v-otp-input {
  :deep(.v-otp-input__content) {
    padding: 0;
  }
}

:deep(.v-otp-input .v-otp-input__content) {
  width: 100%;
  max-width: 450px !important;
  gap: 12px !important;
}

:deep(.v-otp-input .v-field__input) {
  min-height: 56px !important;
  height: 56px !important;
  padding: 0 !important;
}

:deep(.v-otp-input .v-field) {
  min-width: 86px !important;
  width: 86px !important;
  height: 56px !important;
}

@media (max-width: 600px) {
  :deep(.v-otp-input .v-field) {
    min-width: 52.5px !important;
    width: 52.5px !important;
    height: 56px !important;
  }
}

// Apply AppTextField styling to each OTP field
:deep(.v-field) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: none !important;
  overflow: visible;
  border-radius: 12px;
}

:deep(.v-field--focused) {
  border-color: #414156 !important;
  background-color: #fffce9 !important;
}

:deep(.v-field--error) {
  border-color: rgb(var(--v-theme-error)) !important;
}

:deep(.v-field--error .v-field__outline),
:deep(.v-field__outline) {
  display: none;
}

:deep(.v-field__overlay) {
  display: none;
}

.otp-request {
  margin-top: 18px;
  gap: 8px;
  font-size: 16px !important;

  :deep(span) {
    font-size: 16px !important;
  }
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
